import { motion } from "framer-motion";
import { Plus, Minus } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import {
  TRY_DENOMINATIONS,
  type DayCloseLabels,
  type DenominationCountEntry,
} from "./types";
import {
  denominationsTotal, formatMoney, getCount, setCount, formatSignedMoney,
} from "./utils";

export interface CashDenominationGridProps {
  counts: readonly DenominationCountEntry[];
  expectedMinor: number;
  openingFloatMinor: number;
  onChange: (counts: readonly DenominationCountEntry[]) => void;
  labels: DayCloseLabels;
  locale: "tr" | "en";
  className?: string;
}

export function CashDenominationGrid({
  counts, expectedMinor, openingFloatMinor, onChange, labels, locale, className,
}: CashDenominationGridProps) {
  const total = denominationsTotal(counts);
  const variance = total - expectedMinor;
  const maxRowSubtotal = TRY_DENOMINATIONS.reduce(
    (m, d) => Math.max(m, d.unitMinor * getCount(counts, d.code)),
    1,
  );

  return (
    <div className={cn("space-y-4", className)}>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Stat label={labels.steps.cash.openingFloat} value={formatMoney(openingFloatMinor, labels.currencyPrefix, locale)} />
        <Stat label={labels.steps.cash.expected} value={formatMoney(expectedMinor, labels.currencyPrefix, locale)} />
        <Stat label={labels.steps.cash.counted} value={formatMoney(total, labels.currencyPrefix, locale)} bold />
      </div>

      <div className="rounded-biz-card border border-biz-border bg-biz-surface-elevated overflow-hidden">
        <div className="grid grid-cols-[110px_1fr_120px_140px] items-center px-4 py-2 bg-biz-surface text-[11px] font-semibold uppercase tracking-wider text-biz-foreground-muted">
          <span>{labels.steps.cash.denomination}</span>
          <span />
          <span className="text-center">{labels.steps.cash.count}</span>
          <span className="text-right">{labels.steps.cash.subtotal}</span>
        </div>
        <ul className="divide-y divide-biz-border">
          {TRY_DENOMINATIONS.map((d) => {
            const count = getCount(counts, d.code);
            const subtotal = count * d.unitMinor;
            const ratio = Math.min(1, subtotal / maxRowSubtotal);
            return (
              <li key={d.code} className="grid grid-cols-[110px_1fr_120px_140px] items-center px-4 py-2 gap-3">
                <span className="font-mono-num tabular-nums text-sm font-semibold text-biz-foreground">
                  {labels.denominations[d.code] ?? d.label}
                </span>
                <div className="h-2 rounded-full bg-biz-muted overflow-hidden">
                  <motion.div
                    className={cn("h-full", d.kind === "note" ? "bg-biz-primary" : "bg-biz-accent")}
                    initial={false}
                    animate={{ width: `${ratio * 100}%` }}
                    transition={{ duration: 0.25 }}
                  />
                </div>
                <Stepper
                  value={count}
                  onChange={(v) => onChange(setCount(counts, d.code, v))}
                  ariaLabel={`${labels.steps.cash.count} — ${d.label}`}
                />
                <span className="font-mono-num tabular-nums text-sm text-right text-biz-foreground">
                  {formatMoney(subtotal, labels.currencyPrefix, locale)}
                </span>
              </li>
            );
          })}
        </ul>
        <div className="grid grid-cols-[110px_1fr_120px_140px] items-center px-4 py-3 border-t border-biz-border bg-biz-surface gap-3">
          <span className="font-semibold text-sm text-biz-foreground">
            {labels.steps.cash.counted}
          </span>
          <span className="text-xs text-biz-foreground-muted">
            vs {formatMoney(expectedMinor, labels.currencyPrefix, locale)}
          </span>
          <span className="text-center font-mono-num tabular-nums text-sm font-semibold text-biz-foreground-muted">
            {counts.reduce((a, c) => a + c.count, 0)}
          </span>
          <span className="font-mono-num tabular-nums text-base font-bold text-right text-biz-foreground">
            {formatMoney(total, labels.currencyPrefix, locale)}
          </span>
        </div>
      </div>

      <p className="text-xs text-biz-foreground-subtle">{labels.steps.cash.tip}</p>

      <div className="flex items-center justify-end gap-2 text-sm">
        <span className="text-biz-foreground-muted">{labels.summary.variance}:</span>
        <span
          className={cn(
            "font-mono-num tabular-nums font-semibold",
            variance === 0 ? "text-biz-success" : Math.abs(variance) > 1000 ? "text-biz-danger" : "text-biz-warning",
          )}
        >
          {formatSignedMoney(variance, labels.currencyPrefix, locale)}
        </span>
      </div>
    </div>
  );
}

function Stat({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className="rounded-biz-card border border-biz-border bg-biz-surface px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-biz-foreground-muted">{label}</div>
      <div className={cn("font-mono-num tabular-nums mt-1", bold ? "text-lg font-bold text-biz-foreground" : "text-base text-biz-foreground")}>
        {value}
      </div>
    </div>
  );
}

function Stepper({
  value, onChange, ariaLabel,
}: { value: number; onChange: (v: number) => void; ariaLabel: string }) {
  return (
    <div className="flex items-center justify-center gap-1">
      <button
        type="button"
        onClick={() => onChange(Math.max(0, value - 1))}
        aria-label={`${ariaLabel} −`}
        className="w-9 h-9 flex items-center justify-center rounded-biz-btn border border-biz-border bg-biz-surface-elevated text-biz-foreground hover:bg-biz-muted biz-focus-ring"
      >
        <Minus className="w-4 h-4" />
      </button>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        max={500}
        value={value}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        aria-label={ariaLabel}
        className="w-12 h-12 text-center font-mono-num tabular-nums text-base font-semibold rounded-biz-btn border border-biz-border bg-biz-surface-elevated text-biz-foreground biz-focus-ring [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
      />
      <button
        type="button"
        onClick={() => onChange(Math.min(500, value + 1))}
        aria-label={`${ariaLabel} +`}
        className="w-9 h-9 flex items-center justify-center rounded-biz-btn border border-biz-border bg-biz-surface-elevated text-biz-foreground hover:bg-biz-muted biz-focus-ring"
      >
        <Plus className="w-4 h-4" />
      </button>
    </div>
  );
}
